import * as React from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'

import { cn, slugify } from '@/utils'

type NodeContent =
  | string
  | number
  | React.ReactNode
  | { props?: { children?: NodeContent } }
  | NodeContent[]

const getNodeText = (node: NodeContent): string => {
  if (['string', 'number'].includes(typeof node)) return String(node)
  if (Array.isArray(node)) return node.map(getNodeText).join('')
  if (typeof node === 'object' && node && node !== null && 'props' in node) {
    const nodeWithProps = node as { props?: { children?: NodeContent } }
    return getNodeText(nodeWithProps.props?.children)
  }
  return ''
}

interface MarkdownProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  content: string
  componentsOverride?: Partial<Components>
}

const Markdown = React.forwardRef<HTMLDivElement, MarkdownProps>(
  ({ className, content, componentsOverride, ...props }, ref) => {
    const components: Components = {
      h1: ({ className, node, children, ...props }) => {
        const id = slugify(getNodeText(children))
        return (
          <h1
            id={id}
            className={cn(
              'text-3xl font-semibold text-blue-7 tracking-tight mt-8 mb-4 first:mt-0 scroll-m-20',
              className,
            )}
            {...props}
          >
            {children}
          </h1>
        )
      },
      h2: ({ className, node, children, ...props }) => {
        const id = slugify(getNodeText(children))
        return (
          <h2
            id={id}
            className={cn(
              'text-2xl font-semibold text-blue-7 tracking-tight mt-8 mb-4 scroll-m-20',
              className,
            )}
            {...props}
          >
            {children}
          </h2>
        )
      },
      h3: ({ className, node, children, ...props }) => {
        const id = slugify(getNodeText(children))
        return (
          <h3
            id={id}
            className={cn(
              'text-xl font-medium text-blue-7 tracking-tight mt-6 mb-3 scroll-m-20',
              className,
            )}
            {...props}
          >
            {children}
          </h3>
        )
      },
      h4: ({ className, node, ...props }) => (
        <h4
          className={cn(
            'text-lg font-medium text-blue-7 tracking-tight mt-6 mb-3',
            className,
          )}
          {...props}
        />
      ),
      p: ({ className, node, ...props }) => (
        <p
          className={cn('leading-7 not-first:mt-4 text-blue-7', className)}
          {...props}
        />
      ),
      ul: ({ className, node, ...props }) => (
        <ul
          className={cn(
            'my-6 ml-6 list-disc [&>li]:mt-2 text-blue-7',
            className,
          )}
          {...props}
        />
      ),
      ol: ({ className, node, ...props }) => (
        <ol
          className={cn(
            'my-6 ml-6 list-decimal [&>li]:mt-2 text-blue-7',
            className,
          )}
          {...props}
        />
      ),
      li: ({ className, node, ...props }) => (
        <li className={cn('leading-7 text-blue-7', className)} {...props} />
      ),
      blockquote: ({ className, node, ...props }) => (
        <blockquote
          className={cn('mt-6 border-l-2 pl-6 italic text-blue-7', className)}
          {...props}
        />
      ),
      table: ({ className, node, ...props }) => (
        <div className="my-6 w-full overflow-y-auto rounded-lg border">
          <table
            className={cn('w-full caption-bottom text-sm', className)}
            {...props}
          />
        </div>
      ),
      thead: ({ className, node, ...props }) => (
        <thead
          className={cn('[&_tr]:border-b bg-muted/50', className)}
          {...props}
        />
      ),
      tbody: ({ className, node, ...props }) => (
        <tbody
          className={cn('[&_tr:last-child]:border-0', className)}
          {...props}
        />
      ),
      tr: ({ className, node, ...props }) => (
        <tr
          className={cn(
            'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
            className,
          )}
          {...props}
        />
      ),
      th: ({ className, node, ...props }) => (
        <th
          className={cn(
            'h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0',
            className,
          )}
          {...props}
        />
      ),
      td: ({ className, node, ...props }) => (
        <td
          className={cn(
            'p-4 align-middle [&:has([role=checkbox])]:pr-0',
            className,
          )}
          {...props}
        />
      ),
      br: () => <span className="block mt-2" />,
      code: ({ className, node, ...props }) => {
        const inline = !('inline' in props && props.inline === false)
        return inline ? (
          <code
            className={cn(
              'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
              className,
            )}
            {...props}
          />
        ) : (
          <pre
            className={cn(
              'mb-4 mt-6 overflow-x-auto rounded-lg border bg-muted p-4',
              className,
            )}
          >
            <code
              className={cn(
                'relative rounded bg-muted font-mono text-sm',
                className,
              )}
              {...props}
            />
          </pre>
        )
      },
      hr: ({ className, node, ...props }) => (
        <hr className={cn('my-8 border-border', className)} {...props} />
      ),
      a: ({ className, node, ...props }) => (
        <a
          className={cn(
            'font-medium underline underline-offset-4 decoration-primary/50 hover:decoration-primary transition-colors',
            className,
          )}
          {...props}
        />
      ),
    }

    const mergedComponents: Components = {
      ...components,
      ...(componentsOverride ?? {}),
    }

    return (
      <div className={cn('space-y-4', className)} ref={ref} {...props}>
        <ReactMarkdown
          remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkBreaks]}
          rehypePlugins={[rehypeRaw]}
          components={mergedComponents}
        >
          {content}
        </ReactMarkdown>
      </div>
    )
  },
)
Markdown.displayName = 'Markdown'

export { Markdown }
